import React from 'react';
import './ChatMessage.css';

const ChatMessage = ({ message }) => {
  const isUser = message.sender === 'user';
  const messageClassName = !isUser
    ? 'message-bubble message-bubble-ai'
    : 'message-bubble';
  // Format timestamp if available
  const formattedTime = message.timestamp 
    ? new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : '';

  // Format message text to handle Markdown, links, and newlines
  const formatText = (text) => {
    if (!text) return null;
    
    // Trim trailing newlines from the text
    const trimmedText = text.replace(/\n+$/, '');
    
    // Split by newlines first
    return trimmedText.split('\n').map((paragraph, pIndex) => {
      // Process the paragraph for links and bold text
      // URL regex pattern
      const urlRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
      const boldRegex = /(\*\*.*?\*\*)/g;
      
      // Find all matches first
      const matches = [];
      let match;
      
      // Find all link matches
      while ((match = urlRegex.exec(paragraph)) !== null) {
        matches.push({
          type: 'link',
          index: match.index,
          length: match[0].length,
          text: match[1],
          url: match[2]
        });
      }
      
      // Find all bold matches
      while ((match = boldRegex.exec(paragraph)) !== null) {
        matches.push({
          type: 'bold',
          index: match.index,
          length: match[0].length,
          text: match[0].slice(2, -2)
        });
      }
      
      // Sort matches by index
      matches.sort((a, b) => a.index - b.index);
      
      // If no matches, return paragraph as is
      if (matches.length === 0) {
        return (
          <React.Fragment key={pIndex}>
            {paragraph}
            {pIndex < trimmedText.split('\n').length - 1 && <br />}
          </React.Fragment>
        );
      }
      
      // Build result with matches
      const result = [];
      let lastIndex = 0;
      
      matches.forEach((match, i) => {
        // Add text before the match
        if (match.index > lastIndex) {
          result.push(
            <React.Fragment key={`${pIndex}-text-${i}`}>
              {paragraph.substring(lastIndex, match.index)}
            </React.Fragment>
          );
        }
        
        // Add the match
        if (match.type === 'link') {
          result.push(
            <a 
              key={`${pIndex}-link-${i}`} 
              href={match.url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="message-link"
            >
              {match.text}
            </a>
          );
        } else if (match.type === 'bold') {
          result.push(
            <strong key={`${pIndex}-bold-${i}`}>
              {match.text}
            </strong>
          );
        }
        
        lastIndex = match.index + match.length;
      });
      
      // Add remaining text
      if (lastIndex < paragraph.length) {
        result.push(
          <React.Fragment key={`${pIndex}-text-last`}>
            {paragraph.substring(lastIndex)}
          </React.Fragment>
        );
      }
      
      return (
        <React.Fragment key={pIndex}>
          {result}
          {pIndex < trimmedText.split('\n').length - 1 && <br />}
        </React.Fragment>
      );
    });
  };

  return (
    <div className={`message-wrapper ${isUser ? 'user-message' : 'ai-message'}`}>
      {!isUser && (
        <div className="message-avatar">
          <img src="/kira.png" alt="Kira" onError={(e) => {e.target.onerror = null; e.target.src = 'https://via.placeholder.com/30'}} />
        </div>
      )}
      <div className="message-content">
        {message.isStreaming && message.intermediateText && (
          <div className="streaming-intermediate">
            {message.intermediateText.split('\n').filter(line => line.trim()).map((line, index) => (
              <div key={index} className="streaming-line">
                <span className="streaming-prefix">|</span>
                <span className="streaming-text">{line}</span>
              </div>
            ))}
          </div>
        )}
        {message.finalText && (
          <div className={messageClassName}>
            {formatText(message.finalText)}
          </div>
        )}
        {!message.finalText && (
          <div className={messageClassName}>
            {formatText(message.text)}
          </div>
        )}
      </div>
      {formattedTime && (
        <div className={`message-timestamp ${isUser ? 'user-timestamp' : ''}`}>
          {formattedTime}
        </div>
      )}
    </div>
  );
};

export default ChatMessage; 